import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Loader2, BadgePercent } from 'lucide-react';
import { toast } from 'sonner';
import { quotesApi } from '../api';
import { centsToDollarString } from '../constants';

export const DiscountDialog = ({ open, onOpenChange, quote, onSaved }) => {
  const [pct, setPct] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setPct(quote?.totals?.discount_percent ? String(quote.totals.discount_percent) : '');
  }, [open, quote]);

  const subtotal = quote?.totals?.customer_subtotal_cents;
  const num = parseFloat(pct);
  const valid = !Number.isNaN(num) && num > 0 && num < 100;
  const previewCents = valid && subtotal != null ? Math.round(subtotal * num / 100) : null;
  const isLive = quote && quote.status !== 'draft';
  const hasExisting = !!quote?.totals?.discount_percent;

  const save = async (value) => {
    setBusy(true);
    try {
      const r = await quotesApi.setDiscount(quote.id, value);
      toast.success(value == null ? 'Discount removed'
        : r.data.reverted_to_draft
          ? 'Discount applied. Quote is back in Draft — publish to get a fresh customer link.'
          : 'Discount applied');
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update discount');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="bg-[#111111] border-gray-800 text-white max-w-md" data-testid="discount-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white"><BadgePercent size={16} className="text-[#D4AF37]" /> Apply Discount</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-400 block mb-1.5">Discount percentage (%)</label>
            <Input value={pct} onChange={(e) => setPct(e.target.value)} placeholder="e.g. 10" inputMode="decimal"
              className="bg-black border-gray-700 text-white" data-testid="discount-percent-input" />
          </div>
          {valid && subtotal != null && (
            <div className="text-sm bg-black border border-gray-800 rounded-lg p-3 space-y-1" data-testid="discount-preview">
              <div className="flex justify-between text-gray-400"><span>Subtotal</span><span>{centsToDollarString(subtotal)}</span></div>
              <div className="flex justify-between text-[#D4AF37]"><span>Discount ({num}%)</span><span>−{centsToDollarString(previewCents)}</span></div>
              <div className="flex justify-between font-semibold text-white border-t border-gray-800 pt-1"><span>New Total</span><span>{centsToDollarString(subtotal - previewCents)}</span></div>
            </div>
          )}
          {isLive && (
            <p className="text-xs text-amber-400 bg-amber-900/20 border border-amber-800 rounded px-2.5 py-2" data-testid="discount-live-warning">
              This quote was already shared with the customer. Applying or changing the discount will permanently disable the old customer link and return the quote to Draft — publish again to get a fresh link to send.
            </p>
          )}
        </div>
        <DialogFooter className="gap-2">
          {hasExisting && (
            <Button variant="outline" onClick={() => save(null)} disabled={busy} className="border-red-900 text-red-400 mr-auto" data-testid="remove-discount-btn">Remove Discount</Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="border-gray-700 text-gray-300">Cancel</Button>
          <Button onClick={() => save(num)} disabled={busy || !valid} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="save-discount-btn">
            {busy && <Loader2 size={14} className="animate-spin" />} Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
